import React, { useContext, useEffect, useState, useRef } from "react"
import CheckoutSuccessPopup from "../services/CheckoutSuccessPopup"
import FlexPaymentOptions from "../services/FlexPaymentOptions"
import PublicPayPalComponent from "../PayPal/Public/PublicPayPalComponent"
import useManageServices from "../services/hooks/useManageServices"
import { Button } from "../ui/button"
import { ContactRequests } from "../../api/app.service"
import { DateTime } from "luxon"
import { Link } from "react-router-dom"
import { MeetingRequests } from "../../api/app.service"
import { ServiceRequests } from "../../api/app.service"
import { ServiceType } from "../services/hooks/useManageServices"
import {
  SvgCheckCircle,
  SvgPiechartOneQuarter,
  SvgPiechartThreeQuarters,
} from "../icons"
import { useParams } from "react-router-dom"
import { UserCtx } from "../../context/userContext"
import { useToast } from "../../hooks/useToast"

interface Props {
  service: any
  coachPublicProfileData: any
  connectionCompleted?: boolean
  setLoading: any
  validatingForm?: boolean
  cardFieldErrors: {
    cardName: boolean
    cardNumber: boolean
    cardExpiry: boolean
    cardCvv: boolean
  }
  setCardFieldErrors: React.Dispatch<
    React.SetStateAction<{
      cardName: boolean
      cardNumber: boolean
      cardExpiry: boolean
      cardCvv: boolean
    }>
  >
  discountedOrderTotal: number
  setDiscountedOrderTotal: React.Dispatch<React.SetStateAction<number>>
}

const GuestCheckoutSummary: React.FC<Props> = ({
  service,
  coachPublicProfileData,
  connectionCompleted,
  setLoading,
  validatingForm,
  cardFieldErrors,
  setCardFieldErrors,
  discountedOrderTotal,
  setDiscountedOrderTotal,
}) => {
  const { toast } = useToast()
  const { user } = useContext(UserCtx)
  const { endpoint_slug: endpointSlug } = useParams<{
    endpoint_slug: string
  }>()
  const { getServiceObject } = useManageServices()

  const [promoCode, setPromoCode] = useState<string>("")
  const [discountedUpFrontAmount, setDiscountedUpFrontAmount] = useState(0)
  const [discountInstallment, setDiscountInstallment] = useState(0)
  const [promoTotal, setPromoTotal] = useState(0)
  const [isTermsAgreed, setIsTermsAgreed] = useState<boolean>(false)
  const [paymentMethods, setPaymentMethods] = useState<any[]>([])
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<any>({})
  const [checkoutSuccessPopup, setCheckoutSuccessPopup] = useState(false)
  const [activeContact, setActiveContact] = useState<any>(null)
  const [readyForPayment, setReadyForPayment] = useState<boolean>(false)
  const [serviceType, setServiceType] = useState<ServiceType>(
    service.is_group_service ? "individual" : "consultation"
  )
  const [isFreeService, setIsFreeService] = useState(false)
  const [serviceValues, setServiceValues] = useState<any>(null)
  const [spotsLeft, setSpotsLeft] = useState<number | null>(null)
  const [activeDate, setActiveDate] = useState<DateTime>(
    DateTime.local()
      .startOf("day")
      .setZone(user?.timezone?.value || "utc")
  )
  const [meetings, setMeetings] = useState<any[]>([])
  const [appliedDiscount, setAppliedDiscount] = useState<any>({
    amount: 0,
    promoCode: "",
    promoType: "",
    promoValue: 0,
  })
  const [payInFull, setPayInFull] = useState(true)

  const activeContactRef = useRef<any>(null)

  const determineServiceType = (service: any): ServiceType => {
    if (service?.service_details?.type === "recurring") return "recurring"
    if (service?.service_details?.type === "group") return "individual"
    if (service?.service_details?.type === "individual") return "individual"
    if (service?.service_details?.type === "bundle") return "bundle"
    return "consultation"
  }

  const freeService =
    discountedOrderTotal === 0 ||
    (service.service_details.type !== "consultation" &&
      Number(service.service_details.billing.amount) === 0)
  const paidService = Number(service.service_details.billing.amount) !== 0
  const installmentService =
    service.service_details.billing.unit === "installment"
  const groupRecurring = service.service_details.type === "recurring"

  const getCurrencySymbol = () => {
    return String.fromCharCode(
      parseInt(service?.service_details?.symbol?.slice(2), 16)
    )
  }

  const calculateUpFront = (): number => {
    const isRecurring = service?.service_details?.type === "recurring"
    const discountAmount = parseFloat(appliedDiscount?.promoValue || "0")
    const discountType = appliedDiscount?.promoType

    const amount = parseFloat(service?.service_details?.billing?.amount || "0")
    const time = parseInt(service?.service_details?.expiration?.time || "1")
    const totalAmount = isRecurring ? amount * time : amount
    const upFrontPercentage =
      service?.service_details?.billing?.upFrontPercentage / 100 || 0

    let upfrontAmount = 0

    if (isRecurring) {
      upfrontAmount = totalAmount
    } else {
      const upfrontAmountStr = getInstallmentAmounts().upfront
      upfrontAmount = parseFloat(upfrontAmountStr.replace("$", "").trim())
    }

    if (
      !service?.service_details?.billing?.installmentIncrements ||
      service?.service_details?.billing?.installmentIncrements.length === 0
    ) {
      return (
        (totalAmount - parseFloat(appliedDiscount?.amount || "0")) /
        service?.service_details.expiration.time
      )
    }

    if (!isNaN(upfrontAmount) && !isNaN(discountAmount) && appliedDiscount) {
      if (discountType === "percent") {
        const discountPercentage = discountAmount / 100
        const discountedAmount = upfrontAmount * (1 - discountPercentage)
        return discountedAmount
      } else if (discountType === "amount") {
        const newAmount = (totalAmount - discountAmount) * upFrontPercentage
        return newAmount
      }
    }

    return upfrontAmount
  }

  const getInstallmentAmounts = () => {
    const percent = service?.service_details?.billing?.upFrontPercentage / 100
    const amount = parseInt(service?.service_details?.billing?.amount)
    const upfront = amount * percent
    const installment =
      (amount - upfront) /
      service?.service_details?.billing?.installmentIncrements?.length
    return {
      upfront: `$${upfront.toFixed(2)}`,
      installment: `$${installment.toFixed(2)}`,
    }
  }

  const handlePromoCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPromoCode(e.target.value)
  }

  const validatePromoCode = () => {
    if (promoCode !== "") {
      ServiceRequests.validateServicePromoCode({
        promo_code: promoCode,
        coach_id: parseInt(coachPublicProfileData.user_id),
      })
        .then((data) => {
          if (!data.promo_valid) {
            setAppliedDiscount({
              amount: 0,
              promoCode: "",
              promoType: "",
              promoValue: 0,
            })
            setPromoCode("")
            toast.error(data.promo_invalid_reason)
          } else {
            const details = data.promo_code
            const discountType = details.promo_type
            const discountUnits = details.promo_value
            const promoCode = details.promo_code
            if (discountType === "percent") {
              const baseAmount = service?.service_details?.billing?.amount || 0
              const cycles =
                service?.service_details?.type === "recurring"
                  ? service?.service_details?.expiration?.time || 1
                  : 1

              const discountDollars =
                (discountUnits / 100) * baseAmount * cycles

              setAppliedDiscount({
                amount: discountDollars,
                promoCode: promoCode,
                promoType: discountType,
                promoValue: discountUnits,
              })

              toast.success(
                `Congrats! Your promotion code is saving you ${String.fromCharCode(
                  parseInt(service?.service_details?.symbol?.slice(2), 16)
                )}${discountDollars} on this payment`
              )
            } else if (discountType === "amount") {
              setAppliedDiscount({
                amount: discountUnits,
                promoCode: promoCode,
                promoType: discountType,
                promoValue: discountUnits,
              })
              toast.success(
                `Congrats! Your promotion code is saving you ${String.fromCharCode(
                  parseInt(service?.service_details?.symbol?.slice(2), 16)
                )}${discountUnits} on this payment`
              )
            }
          }
        })
        .catch((err) => {
          console.error(err)
          toast.error(err.response.data.message)
        })
    }
  }

  const calculateDiscountedUpInstallment = () => {
    const installAmountStr = getInstallmentAmounts().installment
    const upfrontAmount = parseFloat(installAmountStr.replace("$", "").trim())
    const discountAmount = parseFloat(appliedDiscount?.promoValue || "0")
    const discountType = appliedDiscount?.promoType
    let totalAmount = parseFloat(service?.service_details?.billing?.amount)
    const discountLength =
      service?.service_details.billing.installmentIncrements?.length

    if (!discountLength || isNaN(discountLength) || discountLength <= 0) {
      return upfrontAmount
    }

    if (!isNaN(upfrontAmount) && !isNaN(discountAmount) && appliedDiscount) {
      if (discountType === "percent") {
        const discountPercentage = discountAmount / 100
        const discountedAmount = upfrontAmount * (1 - discountPercentage)
        return discountedAmount
      } else if (discountType === "amount") {
        const adjustedTotal = totalAmount - parseFloat(appliedDiscount.amount)
        const newAmount = (adjustedTotal - calculateUpFront()) / discountLength
        return newAmount
      }
    }

    return upfrontAmount
  }

  const calculateTotalAmount = () => {
    const amount = parseFloat(service?.service_details?.billing?.amount || "0")
    const time = parseInt(service?.service_details?.expiration?.time || "1")
    const isRecurring = service?.service_details?.type === "recurring"

    let totalAmount = isRecurring ? amount * time : amount

    if (appliedDiscount?.amount) {
      if (
        appliedDiscount.promoType === "percentage" ||
        appliedDiscount.promoType === "percent"
      ) {
        totalAmount *= 1 - parseFloat(appliedDiscount.promoValue) / 100
      } else {
        totalAmount -= parseFloat(appliedDiscount.amount)
      }
    }

    return totalAmount < 0 ? 0 : totalAmount
  }

  const getFutureInstallmentDates = () => {
    const increments =
      service?.service_details?.billing?.installmentIncrements || []

    let cursor = DateTime.now()

    return increments.map((inc: any) => {
      cursor = cursor.plus({ [inc.type]: inc.time })
      return cursor
    })
  }

  const getRecurringPayment = () => {
    const details = service.service_details
    const symbol = String.fromCharCode(parseInt(details.symbol?.slice(2), 16))
    const baseAmount = parseFloat(details.billing?.amount || "0")
    const isRecurring = details.type === "recurring"
    const discountRaw = appliedDiscount?.amount
      ? parseFloat(appliedDiscount.amount)
      : 0
    const cycles =
      isRecurring && details.expiration?.time ? details.expiration.time : 1
    const adjustedDiscount = isRecurring ? discountRaw / cycles : discountRaw
    const finalAmount = (baseAmount - adjustedDiscount).toFixed(2)

    return `${symbol}${finalAmount}`
  }
  const getContact = async () => {
    try {
      const data = await ContactRequests.getContactList({
        profile_id: user.activeProfileId,
        profile_type: user.activeProfile,
      })
      const foundContact = data.contact_list?.find(
        (contact: any) => contact.endpoint_slug === endpointSlug
      )
      setActiveContact(foundContact)
      if (paidService && discountedOrderTotal !== 0) {
        setReadyForPayment(true)
      } else if (freeService && foundContact) {
        createFreeOrder(foundContact)
      }
    } catch (ex) {
      console.error(ex)
    }
  }

  const getGroupServiceEnrollment = async () => {
    try {
      const response = await ServiceRequests.checkGroupServiceEnrollment({
        stripe_product_id: service?.stripe_product_id,
      })
      const data = response
      const spotsLeft = data.spots_left
      setSpotsLeft(spotsLeft)
      return spotsLeft
    } catch (error) {
      console.error("Error fetching group service enrollment:", error)
      return null
    }
  }

  useEffect(() => {
    setServiceType(determineServiceType(service))
    setServiceValues({
      isGroupService: service?.is_group_service,
      color: service?.service_details?.image?.color,
      title: service?.service_details?.title,
      description: "",
      feature1: service?.service_details?.details?.features?.feature1,
      feature2: service?.service_details?.details?.features?.feature2,
      feature3: service?.service_details?.details?.features?.feature3,
      sessionLength: {
        minutes: service?.service_details?.session?.duration,
      },
      sessionCost: service?.service_details?.billing.amount,
      currency: service?.service_details?.symbol,
      symbol: service?.service_details?.symbol,
      isFree: service?.service_details?.isFree,
      allowPromo: service?.service_details?.allowPromo,
      package: {
        packageSessions: service?.service_details?.session?.count,
        packageBillingType:
          service?.service_details?.billing?.packageBillingType !== undefined
            ? service?.service_details?.billing?.packageBillingType
            : service?.service_details?.billing?.unit,
        packageCost: service?.service_details?.billing.amount,
        installmentIncrements:
          service?.service_details?.billing.installmentIncrements,
        upFrontPercentage: service?.service_details?.billing?.upFrontPercentage,
      },
      recurring: {
        periodCost: service?.service_details?.billing.amount,
        recurringFrequency: service?.service_details?.billing?.unit,
        sessionsPerPeriod: service?.service_details?.session?.count,
        unlimited: false,
      },
      expiration: {
        expireTime: service?.service_details?.expiration.time,
        expireType: service?.service_details?.expiration.type,
        expires: service?.service_details?.expiration?.expires,
      },
      enabled: service?.service_details?.enabled,
      profileServiceVisible:
        service?.service_details?.visibility?.profile.service,
      profileCostVisible: service?.service_details?.visibility?.profile.cost,
      marketplaceServiceVisible:
        service?.service_details?.visibility?.marketplace.service,
      marketplaceCostVisible:
        service?.service_details?.visibility?.marketplace.service,
      image: {
        exists: service?.service_details?.image.exists,
        base64: "",
        base64extension: "",
      },
      group: {
        isFree: service?.service_details?.isFree,
        weeklyDayTimes: service?.service_details?.group?.weeklyDayTimes,
        maximumClients: service?.service_details?.maximumClients,
        groupChat: service?.service_details?.groupChat,
      },
      serviceLinkedForms: service?.service_details?.serviceLinkedForms,
    })
  }, [service])

  useEffect(() => {
    activeContactRef.current = activeContact
  }, [activeContact])

  useEffect(() => {
    if (service?.payment_term === "installment") {
      setPayInFull(false)
    }
  }, [service?.payment_term])

  const getFullServiceTotal = (): number => {
    const baseAmount = Number(service?.service_details?.billing?.amount || 0)
    const increments =
      service?.service_details?.billing?.installmentIncrements?.length || 1

    if (service?.payment_term === "flexible") {
      return baseAmount * increments
    }

    return baseAmount
  }

  const getOrderTotal = (): number => {
    const baseAmount = Number(service?.service_details?.billing?.amount || 0)
    return baseAmount
  }

  const getMeetings = () => {
    if (user) {
      const startOfActiveDate = activeDate
        .startOf("day")
        .setZone(user?.timezone?.value || "utc")
        .toISO()!
      const endOfSixMonths = activeDate
        .plus({ months: 6 })
        .endOf("day")
        .setZone(user?.timezone?.value || "utc")
        .toISO()!

      MeetingRequests.getMeetings({
        user_id: parseInt(user.user_id),
        profile_id: user.activeProfileId,
        from_date: startOfActiveDate,
        to_date: endOfSixMonths,
      })
        .then((data) => {
          if (data && Array.isArray(data.meetings)) {
            setMeetings(data.meetings)
          } else {
            console.error("Invalid meetings data format", data)
            setMeetings([])
          }
        })
        .catch((ex) => {
          toast.error(ex.response?.data?.message || "An error occurred")
        })
    }
  }

  const matchingMeeting = meetings.find(
    (meeting) =>
      meeting.service_details?.title === service?.service_details.title
  )

  const createFreeOrder = async (contactParam?: any) => {
    const contact = contactParam || activeContactRef.current

    try {
      if (service.is_group_service) {
        if (matchingMeeting) {
          alert("You are already enrolled in this service.")
          window.location.href = `${process.env.REACT_APP_BASE_URL}/providers/${contact?.endpoint_slug}/services`
          return
        }

        const spotsLeft = await getGroupServiceEnrollment()
        if (spotsLeft !== null && spotsLeft <= 0) {
          alert(
            "This group service is full. Please choose a different service."
          )
          window.location.href = `${process.env.REACT_APP_BASE_URL}/providers/${contact?.endpoint_slug}/services`
          return
        }
      }

      const serviceObject = getServiceObject(serviceType, serviceValues)

      await ServiceRequests.purchaseFreeService({
        user_id: user?.user_id,
        coach_user_id: contact?.user_id,
        client_profile_id: user.activeProfileId,
        coach_profile_id: contact?.profile_id,
        payment_method_id: null,
        service: { ...serviceObject, sessionCost: serviceValues.sessionCost },
        stripe_product_id: service?.stripe_product_id,
        user_environment: "",
        promo_code: promoCode,
      })

      if (service?.service_details?.isGroupService) {
        MeetingRequests.updateGroupMeeting({
          profile_id: user.activeProfileId,
          user_id: parseInt(user.user_id),
          coach_user_id: contact?.user_id,
          group_service_id: service.group_service_id,
        })
      }
      setIsFreeService(true)
      setCheckoutSuccessPopup(true)
    } catch (error) {
      console.error("Error in createFreeOrder:", error)

      if (error instanceof Error) {
        if (error.message === "You are already enrolled in this service.") {
          alert(error.message)
          return
        }

        if (error.message === "Group Service Full") {
          alert(
            "This group service is full. Please choose a different service."
          )
        }
      } else {
        console.error("Unknown error:", error)
      }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    getMeetings()
  }, [])

  useEffect(() => {
    if (connectionCompleted === true) {
      getContact()
    }
  }, [connectionCompleted])

  useEffect(() => {
    if (service && appliedDiscount) {
      const newUpfrontAmount = calculateUpFront()
      setDiscountedUpFrontAmount(newUpfrontAmount)

      const newInstallmentAmount = calculateDiscountedUpInstallment()
      setDiscountInstallment(newInstallmentAmount)

      const newTotalAmount = calculateTotalAmount()
      setDiscountedOrderTotal(newTotalAmount)
      const baseAmount = Number(service?.service_details?.billing?.amount || 0)

      const promoTotalValue =
        service?.payment_term === "flexible" ||
        service?.payment_term === "installment"
          ? baseAmount - newTotalAmount
          : baseAmount - newTotalAmount

      setPromoTotal(Math.max(0, promoTotalValue))
    }
  }, [appliedDiscount, service])

  return (
    <>
      {paidService ? (
        <>
          {installmentService && (
            <div className="flex flex-col w-full mb-4">
              <p className="text-graySlate mb-[16px] text-base">
                PAYMENT SCHEDULE
              </p>
              {/* 1st installment */}
              <div className="mb-[14px] flex items-center">
                <SvgPiechartOneQuarter className="mr-[16px]" />
                <p className="min-w-fit text-base">1st installment today</p>
                <div className="border-grayCloud mx-[24px] h-[2px] w-full border-b border-dashed" />
                <p className="min-w-fit text-base">
                  {getCurrencySymbol()}
                  {calculateUpFront().toFixed(2)}
                </p>
              </div>
              {/* FUTURE INSTALLMENTS — vertical stack */}
              <div className="flex flex-col">
                {getFutureInstallmentDates().map((date: any, idx: number) => (
                  <div key={idx} className="mb-[12px] flex items-center">
                    <SvgPiechartThreeQuarters className="mr-[16px]" />

                    <p className="min-w-fit text-base">
                      {date.toFormat("MM/dd/yyyy")}
                    </p>

                    <div className="border-grayCloud mx-[24px] h-[2px] w-full border-b border-dashed" />

                    <p className="min-w-fit text-base">
                      {getCurrencySymbol()}
                      {calculateDiscountedUpInstallment().toFixed(2)}
                    </p>
                  </div>
                ))}
              </div>
              <p className="text-graySlate mb-[24px] text-base">
                Payments will be charged on the installment dates to the payment
                method you select below
              </p>
            </div>
          )}
          {groupRecurring && (
            <div className="flex flex-col w-full mb-4">
              <p className="text-base text-graySlate mb-4">PAYMENT SCHEDULE</p>

              <div className="flex items-center mb-4 w-full">
                <SvgPiechartOneQuarter className="mr-4" />
                <p className="text-base mr-4">1st payment today</p>
                <div className="flex-1 border-b border-dashed border-grayCloud mx-2" />
                <p className="text-base min-w-fit">
                  {getCurrencySymbol()}
                  {calculateUpFront().toFixed(2)}
                </p>
              </div>

              <div className="flex items-center mb-4 w-full">
                <SvgPiechartThreeQuarters className="mr-4" />
                <p className="text-base mr-4">Recurring payments</p>
                <div className="flex-1 border-b border-dashed border-grayCloud mx-2" />
                <p className="text-base min-w-fit">
                  <span className="text-graySlate">
                    {service?.service_details?.expiration.time - 1}
                    x&nbsp;
                  </span>
                  {getRecurringPayment()}{" "}
                </p>
              </div>

              <p className="text-graySlate text-base mt-2">
                Payments will be charged &thinsp;
                <strong className="text-black">
                  {service?.service_details?.billing.unit === "month"
                    ? "monthly"
                    : "weekly"}
                </strong>{" "}
                &thinsp;on the anniversary of the purchase date to the payment
                method you select below
              </p>
            </div>
          )}
          <div className="w-full">
            <div className="flex items-center w-full justify-between mb-[16px]">
              <p className={`text-base text-graySlate`}>SUMMARY</p>
            </div>
            {service?.payment_term === "flexible" &&
              discountedOrderTotal !== 0 && (
                <>
                  <FlexPaymentOptions
                    payInFull={payInFull}
                    setPayInFull={setPayInFull}
                  />

                  {!payInFull && (
                    <>
                      <p className="text-graySlate mb-[16px] text-base">
                        PAYMENT SCHEDULE
                      </p>

                      {/* 1st installment */}
                      <div className="mb-[14px] flex items-center">
                        <SvgPiechartOneQuarter className="mr-[16px]" />
                        <p className="min-w-fit text-base">
                          1st installment today
                        </p>
                        <div className="border-grayCloud mx-[24px] h-[2px] w-full border-b border-dashed" />
                        <p className="min-w-fit text-base">
                          {getCurrencySymbol()}
                          {calculateUpFront().toFixed(2)}
                        </p>
                      </div>

                      {/* Future installments */}
                      <div className="flex flex-col">
                        {getFutureInstallmentDates().map(
                          (date: any, idx: number) => (
                            <div
                              key={idx}
                              className="mb-[12px] flex items-center"
                            >
                              <SvgPiechartThreeQuarters className="mr-[16px]" />
                              <p className="min-w-fit text-base">
                                {date.toFormat("MM/dd/yyyy")}
                              </p>
                              <div className="border-grayCloud mx-[24px] h-[2px] w-full border-b border-dashed" />
                              <p className="min-w-fit text-base">
                                {getCurrencySymbol()}
                                {calculateDiscountedUpInstallment().toFixed(2)}
                              </p>
                            </div>
                          )
                        )}
                      </div>

                      <p className="text-graySlate mb-[24px] text-base">
                        Payments will be charged on the installment dates to the
                        payment method you select below
                      </p>

                      <div className="bg-grayMist mb-[24px] h-[1px] w-full" />
                    </>
                  )}
                </>
              )}

            {service?.service_details?.allow_promo && (
              <>
                <div className="relative w-full">
                  <div className="flex gap-2 lg:gap-4 items-stretch w-full mb-4">
                    <div className="default-input flex-1 md:w-auto lg:flex-1">
                      <input
                        type="text"
                        placeholder="Enter Promocode"
                        onChange={handlePromoCodeChange}
                        value={promoCode}
                        className="h-14 w-full"
                      />
                      <div className="overlay">Promocode</div>
                    </div>

                    <Button
                      onClick={validatePromoCode}
                      className="flex items-center justify-center p-0 h-14 w-14 rounded-md"
                    >
                      <SvgCheckCircle className="w-6 h-6" />
                    </Button>
                  </div>
                </div>
                <div className="flex items-center mb-[8px]">
                  <p className="text-base min-w-fit">
                    {`Promotions ${
                      appliedDiscount?.promoValue !== undefined
                        ? appliedDiscount.promoType === "percent"
                          ? `(${Number(appliedDiscount.promoValue)}% OFF)`
                          : `($${Number(appliedDiscount.promoValue).toFixed(
                              2
                            )} OFF)`
                        : "0.00"
                    }`}
                  </p>
                  <div className="w-full h-[2px] mx-[24px] border-b border-dashed border-grayCloud" />
                  <p className="text-base min-w-fit">
                    (
                    {String.fromCharCode(
                      parseInt(service?.service_details?.symbol?.slice(2), 16)
                    )}
                    {appliedDiscount?.amount !== undefined
                      ? Number(appliedDiscount.amount).toFixed(2)
                      : "0.00"}
                    )
                  </p>
                </div>
              </>
            )}
            {!installmentService && !groupRecurring && payInFull && (
              <div className="mb-[8px] flex items-center">
                <p className="min-w-fit text-base">Individual Session</p>
                <div className="border-grayCloud mx-[24px] h-[2px] w-full border-b border-dashed" />
                <p className="min-w-fit text-base">
                  {service &&
                    `${String.fromCharCode(
                      parseInt(service?.service_details?.symbol?.slice(2), 16)
                    )}${parseInt(
                      service?.service_details?.billing?.amount
                    ).toFixed(2)}`}
                </p>
              </div>
            )}
            {(groupRecurring ||
              installmentService ||
              (service?.payment_term === "flexible" && !payInFull)) && (
              <div className="mb-[14px] flex items-center">
                <p className="min-w-fit text-base">
                  {groupRecurring || installmentService
                    ? "Package Total"
                    : "Session Total"}
                </p>
                <div className="border-grayCloud mx-[24px] h-[2px] w-full border-b border-dashed" />
                <p className="min-w-fit text-base">
                  {getCurrencySymbol()}
                  {calculateTotalAmount().toFixed(2)}
                </p>
              </div>
            )}
            <div className="mb-[8px] flex items-center">
              <p className="min-w-fit text-base">Interest or fees</p>
              <div className="border-grayCloud mx-[24px] h-[2px] w-full border-b border-dashed" />
              <p className="min-w-fit text-base">
                {" "}
                {service &&
                  String.fromCharCode(
                    parseInt(service?.service_details?.symbol?.slice(2), 16)
                  )}
                0.00
              </p>
            </div>
            <div className="mb-[24px] flex items-center">
              <p className="min-w-fit text-[18px] font-bold">
                {groupRecurring || installmentService || !payInFull
                  ? "Due Today"
                  : "Order total"}
              </p>{" "}
              <div className="border-grayCloud mx-[24px] h-[2px] w-full border-b border-dashed" />
              <p className="min-w-fit text-base font-bold">
                {getCurrencySymbol()}
                {(groupRecurring || installmentService || !payInFull
                  ? calculateUpFront()
                  : discountedOrderTotal
                ).toFixed(2)}
              </p>
            </div>
            <PublicPayPalComponent
              appliedDiscount={appliedDiscount}
              isTermsAgreed={isTermsAgreed}
              setIsTermsAgreed={setIsTermsAgreed}
              service={service}
              isGroupService={service.service_details.isGroupService}
              activeContact={activeContact}
              activeService={service}
              paymentMethods={paymentMethods}
              selectedPaymentMethod={selectedPaymentMethod}
              setSelectedPaymentMethod={setSelectedPaymentMethod}
              merchantId={coachPublicProfileData.merchant_id}
              checkoutSuccessPopup={checkoutSuccessPopup}
              contact={activeContact}
              setCheckoutSuccessPopup={setCheckoutSuccessPopup}
              activeContactRef={activeContact}
              validatePromoCode={validatePromoCode}
              discountedUpFrontAmount={discountedUpFrontAmount}
              discountInstallment={discountInstallment}
              discountedOrderTotal={discountedOrderTotal}
              promoTotal={promoTotal}
              readyForPayment={readyForPayment}
              setGuestLoading={setLoading}
              validatingForm={validatingForm}
              setLoading={setLoading}
              cardFieldErrors={cardFieldErrors}
              setCardFieldErrors={setCardFieldErrors}
              payInFull={payInFull}
            />
          </div>
        </>
      ) : (
        <div>
          <p className="text-muted-foreground text-xs text-center w-full mx-auto mt-2">
            By continuing, I agree to Zoee's{" "}
            <Link
              to="/terms-and-conditions"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blurple"
            >
              Terms
            </Link>{" "}
            &{" "}
            <Link
              to="/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blurple"
            >
              Privacy Policy
            </Link>
          </p>
        </div>
      )}
      {checkoutSuccessPopup && (
        <CheckoutSuccessPopup
          checkoutSuccessPopup={checkoutSuccessPopup}
          contact={activeContact}
          activeContact={activeContact}
          activeService={service}
          isFreeService={isFreeService}
        />
      )}
    </>
  )
}

export default GuestCheckoutSummary
